import './Style.scss';
import * as React from 'react';
import { Button, ButtonGroup, Col, Form, FormCheck, ListGroup, ProgressBar, Row } from 'react-bootstrap';
import { SingleInstanceInterface } from 'Interfaces/ModuleSelectionInterface';
import { QuestionInterface, questionTypes } from 'Interfaces/Forms/QuestionInterface';
import { DataPoint } from 'Interfaces/DataPoint';
import { FormInterface } from 'Interfaces/Forms/FormsInterface';
import uuid from 'uuid/v4';
import { SingleInstanceProps } from 'views/SingleInstance/Containers/SingleInstanceContainer';
import FormComponent from 'views/SingleInstance/FormComponent';
import GenericModal from 'views/Modals/GenericModal';
import { getFormUtils } from 'views/SingleInstance/utils/FormUtilsHolder';
import { getTableElement } from 'views/SingleInstance/utils/qnrenderer';
import { ConfirmationModal } from 'views/Modals/ConfirmationModal';
import { sendEmail } from 'api/gmailApi';
import { generateName, initDataPoint } from 'views/SingleInstance/utils/utils';
import { getLocalization } from 'global/global';
import GmailThreadView from './GmailThreadView';
import NotesView from './NotesView';
import EmailTemplates from './EmailTemplates';


type MessageType = 'note' | 'email';

export interface Message {
  id: number;
  type: MessageType;
  body: string;
  subject?: string;
  to?: string;
  createdOn: number;
  createdBy: string;
}

interface Props extends SingleInstanceProps {
  singleInstance: SingleInstanceInterface;
}

const getTaskSubForm = (
  question: QuestionInterface,
  forms: FormInterface[]
): FormInterface | undefined =>
  forms.find(f => f.ref === question.listItems?.relation[0].ref);

export default function CRMView(props: Props) {
  const { singleInstance, forms, clientPersist, unsetSingleInstance } = props;
  const { model, dataPoint, newAnswer } = singleInstance;

  // Column 3: collect all TaskQuestion instances (needed by initFromDataPoint)
  const allQuestions: QuestionInterface[] = [
    ...(model.question || []),
    ...(model.questionPage || []).flatMap(p => p.question || [])
  ];
  const taskQuestions = allQuestions.filter(q => q.type === questionTypes.TASK_QUESTION);
  const referenceTableQuestions = allQuestions.filter(
    q => q.type === questionTypes.REFERENCE_TABLE_QUESTION
      && !q.deleted && !q.inVisible && !q.hideInSingleInstance
  );

  // Header state & ref
  const formRef = React.useRef<FormComponent>(null);
  const abortControllerRef = React.useRef(new AbortController());
  const [loaded, setLoaded] = React.useState(false);
  const [localDataPoint, setLocalDataPoint] = React.useState<DataPoint>({} as DataPoint);
  const [showCloseConfirm, setShowCloseConfirm] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = React.useState(false);

  // Column 2 state — initialised to empty; populated by initFromDataPoint after load
  const [composeType, setComposeType] = React.useState<MessageType>('note');
  const [body, setBody] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [to, setTo] = React.useState('');
  const [messages, setMessages] = React.useState<Message[]>([]);
  const [composeErrors, setComposeErrors] = React.useState<{ subject?: string; body?: string }>({});

  // Column 3 state
  const [showTaskModal, setShowTaskModal] = React.useState(false);
  const [activeTaskQuestion, setActiveTaskQuestion] = React.useState<QuestionInterface | null>(null);
  const [activeTaskDataPoint, setActiveTaskDataPoint] = React.useState<DataPoint>({} as DataPoint);
  const [activeSubForm, setActiveSubform] = React.useState<FormInterface | undefined>(undefined);
  const [taskInstances, setTaskInstances] = React.useState<Record<string, DataPoint[]>>({});

  const [sent, setMessageSent] = React.useState(false);
  const [composerFooterClass, setComposerFooterClass] = React.useState('end');

  const composerDisabled = !localDataPoint.id;

  const initFromDataPoint = (dp: DataPoint) => {
    dp['portalEditEntry'] = Date.now();
    dp['comments'] = dp['comments'] || [];
    setLocalDataPoint(dp);
    setTo(dp.id ? String(dp['contactEmail'] || '') : '');
    setMessages(Array.isArray(dp['comments']) ? dp['comments'].map(c => ({
      id: c.id,
      type: 'note',
      body: c.comment,
      createdOn: new Date(c.date).getTime(),
      createdBy: c.username
    })) : []);
    const initial: Record<string, DataPoint[]> = {};
    for (const q of taskQuestions) {
      initial[q.id] = (dp[q.id] as DataPoint[]) || [];
    }
    setTaskInstances(initial);
    setLoaded(true);
  };

  React.useEffect(() => {
    if (newAnswer) {
      const dp = initDataPoint(dataPoint, model, clientPersist, props.locationHierarchy, undefined, forms);
      initFromDataPoint(dp);
    } else {
      const query = dataPoint.id ? { id: dataPoint.id } : undefined;
      props.fetchPOI(
        model.ref, abortControllerRef.current.signal, query ? undefined : dataPoint.row_id, undefined, query
      ).then(res => res.json())
        .then((json: DataPoint[]) => {
          initFromDataPoint(json[0] ?? dataPoint);
        })
        .catch(() => initFromDataPoint(dataPoint));
    }
    return () => abortControllerRef.current.abort();
  }, []);

  React.useEffect(() => {
    setActiveSubform(activeTaskQuestion ? getTaskSubForm(activeTaskQuestion, forms) : undefined);
  }, [activeTaskQuestion]);

  React.useEffect(() => {
    setComposerFooterClass(composeType === 'email' ? 'between' : 'end');
  }, [composeType]);

  if (!loaded) {
    return (
      <div className="crm-loading text-center p-5">
        <ProgressBar animated now={100} />
        <p>{getLocalization('loading')}</p>
      </div>
    );
  }

  // Column 1: filtered model without TaskQuestion or ReferenceTableQuestion
  const excludedFromColumn1 = new Set<string>([
    questionTypes.TASK_QUESTION,
    questionTypes.REFERENCE_TABLE_QUESTION,
  ]);
  const filteredModel: FormInterface = {
    ...model,
    question: model.question?.filter(q => !excludedFromColumn1.has(q.type)),
    questionPage: model.questionPage?.map(page => ({
      ...page,
      question: page.question?.filter(q => !excludedFromColumn1.has(q.type))
    }))
  };

  const handleSave = () => {
    if (!formRef.current) {
      return;
    }
    const result = formRef.current.validateForm();
    if (!result.valid) {
      props.showAlert({
        visible: true,
        message: result.question
          ? `Please fill in the required field: ${result.question.text}`
          : 'Please fill in all required fields.'
      });
      return;
    }
    const newTaskInstances: Record<string, DataPoint[]> = {};
    Object.keys(taskInstances).map(key => {
      newTaskInstances[key] = taskInstances[key].map(t => {
        const task = {...t};
        delete task['updated'];
        return task;
      });
    });
    const dataPoint = {...formRef.current.getDataModel(), ...newTaskInstances};
    const deltaTime = Date.now() - dataPoint['portalEditEntry'];
    if (dataPoint['portalEditTime']) {
      dataPoint['portalEditTime'] = dataPoint['portalEditTime'] + deltaTime;
    } else {
      dataPoint['portalEditTime'] = deltaTime;
    }
    if (!dataPoint.Name || !dataPoint.Name.trim()) {
      dataPoint['Name'] = generateName();
    }
    dataPoint['modified'] = Date.now();
    const tempComments = dataPoint['comments'];
    props.savePOI(dataPoint).then(res => res.json()).then(json => {
      const dp = json.dataPoint;
      const rowId = json.rowId;
      if (rowId !== -1) {
        if (tempComments) {
          dp['comments'] = tempComments;
        }
        initFromDataPoint(dp);
      }
    }).catch(e => {
      console.log(e);
    });
  };

  const handleDelete = () => {
    if (!localDataPoint.id) {
      return;
    }
    props.deletePOI(
      localDataPoint.questionnaire_id || model.ref,
      unsetSingleInstance,
      localDataPoint.id
    );
    setShowDeleteConfirm(false);
  };

  const handleSend = () => {
    if (composeType === 'email') {
      const errors: { subject?: string; body?: string } = {};
      if (!subject.trim()) {
        errors.subject = 'Subject is required.';
      }
      if (!body.trim()) {
        errors.body = 'Message body is required.';
      }
      if (Object.keys(errors).length > 0) {
        setComposeErrors(errors);
        return;
      }
    } else if (!body.trim()) {
      return;
    }
    setComposeErrors({});
    const tempId = Date.now();
    const message: Message = {
      id: tempId,
      type: composeType,
      body,
      subject: composeType === 'email' ? subject : undefined,
      to: composeType === 'email' ? to : undefined,
      createdOn: Date.now(),
      createdBy: clientPersist.userName || ''
    };
    setMessages(prev => [message, ...prev]);
    if (composeType === 'note' && localDataPoint.id) {
      const comment = {
        comment: body, date: Date.now(), username: clientPersist.userName || ''
      };
      props.saveComment(localDataPoint.id, clientPersist.userName || '', comment).then(res => res.json()).then(json => {
        if (json.row_id) {
          setLocalDataPoint((dp) => {
            dp['comments'] = dp['comments'] || [];
            dp['comments'].push({...comment, id: `${json.row_id}`});
            return dp;
          });
          setMessages(prev => prev.map(c => {
            if (c.id === tempId) {
              return {...c, id: json.row_id};
            }
            return c;
          }));
        }
      }).catch(e => console.error(e));
    } else if (composeType === 'email') {
      sendEmail({ to, subject, bodyText: body }).then(json => {
        if (json.status === 'OK') {
          setMessageSent(true);
        }
      }).catch(e => console.log(e));
    }
    setBody('');
    setSubject('');
    setTo('');
  };

  const openNewTask = (question: QuestionInterface) => {
    const today = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    setActiveTaskQuestion(question);
    const subForm = getTaskSubForm(question, forms);
    setActiveTaskDataPoint({
      id: uuid(),
      assignto: clientPersist.user_id,
      questionnaire_id: subForm?.ref,
      taskstatus: 'assigned',
      parentId: model.ref,
      assigndate: `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`,
      created: Date.now(),
    } as DataPoint);
    setShowTaskModal(true);
  };

  const handleTaskSave = () => {
    if (!activeTaskQuestion) {
      return;
    }
    const existing = taskInstances[activeTaskQuestion.id] || [];
    const idx = existing.findIndex(t => t.id && t.id === activeTaskDataPoint.id);
    const updated = idx >= 0
      ? existing.map((t, i) => {
        if (i === idx) {
          if (activeTaskDataPoint['taskstatus'] === 'done' && t['taskstatus'] !== 'done') {
            return {...activeTaskDataPoint, updated: true };
          }
          return activeTaskDataPoint;
        }
        return t;
      })
      : [...existing, { ...activeTaskDataPoint, row_id: Date.now() }];
    setTaskInstances(prev => ({ ...prev, [activeTaskQuestion.id]: updated }));
    setShowTaskModal(false);
  };

  const close = () => {
    return showCloseConfirm ? (
      <ConfirmationModal
        onClose={() => setShowCloseConfirm(false)}
        onCancel={() => setShowCloseConfirm(false)}
        onConfirm={unsetSingleInstance}
        localizations={{
          header: (
            <label>{getLocalization('confirm')}</label>
          ),
          body: <span>{getLocalization('poiChangesAlert')}</span>,
          confirm: getLocalization('ok'),
          cancel: getLocalization('cancel'),
          confirmStyle: 'danger'
        }}
      />
    ) : null;
  };

  const ComposerFooter = () => (
    <div className={`d-flex justify-content-${composerFooterClass} mb-3`}>
      {composeType === 'email' && (
        <EmailTemplates
          body={body}
          onInsertTemplate={(templateBody) => setBody(templateBody)}
        />
      )}
      <Button size="sm" disabled={composerDisabled} onClick={handleSend} data-testid="compose-send-btn">
        {getLocalization(composeType === 'note' ? 'saveNote' : 'send')}
      </Button>
    </div>
  );

  return (
    <>
      <div
        className="crm-header d-flex justify-content-between align-items-center px-3 py-2 border-bottom"
        data-testid="crm-header"
      >
        <div className="d-flex align-items-center gap-2">
          {model.smallIconUrl && (
            <img src={model.smallIconUrl} alt="" className="crm-header-icon" />
          )}
          <h3 className="mb-0">{model.name}</h3>
        </div>
        <div className="d-flex align-items-center gap-2">
          <ButtonGroup>
            <Button size="sm" variant="primary" onClick={handleSave}>{getLocalization('save')}</Button>
            {localDataPoint.id && (
              <Button
                size="sm"
                variant="danger"
                onClick={() => {
                  setShowDeleteConfirm(true);
                  setDeleteConfirmed(false);
                }}
                aria-label="Delete"
                data-testid="crm-delete-btn"
              >
                <i className="fa fa-trash" aria-hidden="true" />
              </Button>
            )}
          </ButtonGroup>
          <Button variant="close" onClick={() => setShowCloseConfirm(true)} aria-label="Close" />
        </div>
      </div>

      <Row className="crm-view g-0">
        {/* Column 1: Form fields (no TaskQuestions) */}
        <Col md={4} className="crm-column">
          <FormComponent
            ref={formRef}
            model={filteredModel}
            dataPoint={localDataPoint}
            forms={forms}
            clientPersist={clientPersist}
            readOnly={false}
          />
        </Col>

        {/* Column 2: Communication composer + history */}
        <Col md={4} className="crm-column">
          <ButtonGroup className="crm-compose-toggle w-100">
            <Button
              size="sm"
              variant={composeType === 'note' ? 'primary' : 'outline-primary'}
              disabled={composerDisabled}
              data-testid="compose-note-btn"
              onClick={() => { setComposeType('note'); setComposeErrors({}); }}
            >
              {getLocalization('internalNote')}
            </Button>
            <Button
              size="sm"
              variant={composeType === 'email' ? 'primary' : 'outline-primary'}
              disabled={composerDisabled}
              data-testid="compose-email-btn"
              onClick={() => { setComposeType('email'); setComposeErrors({}); }}
            >
              {getLocalization('email')}
            </Button>
          </ButtonGroup>

          {composeType === 'email' && (
            <>
              <Form.Group className="mb-2">
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder={getLocalization('to')}
                  value={to}
                  disabled={composerDisabled}
                  data-testid="compose-to-input"
                  onChange={e => setTo(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder={getLocalization('subject')}
                  value={subject}
                  isInvalid={!!composeErrors.subject}
                  disabled={composerDisabled}
                  data-testid="compose-subject-input"
                  onChange={e => setSubject(e.target.value)}
                />
                <Form.Control.Feedback type="invalid" data-testid="compose-subject-error">
                  {composeErrors.subject}
                </Form.Control.Feedback>
              </Form.Group>
            </>
          )}
          <Form.Group className="mb-2">
            <Form.Control
              as="textarea"
              size="sm"
              rows={4}
              placeholder={getLocalization(composeType === 'note' ? 'writeNote' : 'composeEmail')}
              value={body}
              isInvalid={!!composeErrors.body}
              disabled={composerDisabled}
              data-testid="compose-body-input"
              onChange={e => setBody(e.target.value)}
            />
            <Form.Control.Feedback type="invalid" data-testid="compose-body-error">
              {composeErrors.body}
            </Form.Control.Feedback>
          </Form.Group>
          <ComposerFooter />
          {composeType === 'note' && (
            <NotesView
              messages={messages}
              onNoteDeleted={(id) => {
                setMessages(messages.filter(m => `${m.id}` !== `${id}`));
                formRef.current?.deleteComment(id);
              }}
              onNoteUpdated={(id, comment) => setMessages(messages.map(m =>
                `${m.id}` === `${id}` ? {...m, body: comment} : m
              ))}
            />
          )}
          {composeType === 'email' && (
            <div className="crm-thread-view">
              <GmailThreadView
                contactEmail={String(localDataPoint['contactEmail'] || '')}
                companyId={String(localDataPoint['location1'])}
                crmFormType={model.crmFormType}
                sent={sent}
                resetMessageSent={() => setMessageSent(false)}
              />
            </div>
          )}
        </Col>
        {/* Column 3: Task instances */}
        <Col md={4} className="crm-column">
          <div className="d-flex justify-content-end mb-3">
            <Button
              size="sm"
              variant="outline-primary"
              disabled={taskQuestions.length === 0}
              data-testid="add-task-btn"
              onClick={() => taskQuestions.length > 0 && openNewTask(taskQuestions[0])}
            >
              +
            </Button>
          </div>

          {taskQuestions.length === 0 && (
            <p className="text-muted small">{getLocalization('noTaskQuestions')}</p>
          )}

          {taskQuestions.map(question => {
            const instances = (taskInstances[question.id] || [])
              .filter(t => t['taskstatus'] !== 'done' || t['taskstatus'] === 'done' && t['updated']);
            const taskForm = getTaskSubForm(question, forms);
            const fu = getFormUtils(taskForm);
            const due = fu.getQuestion('duedate');
            const assigned = fu.getQuestion('assignedto');
            return (
              <div key={question.id} className="mb-4">
                <h6 className="fw-semibold border-bottom pb-1 mb-2">{question.text || question.id}</h6>
                {instances.length === 0 ? (
                  <p className="text-muted small">{getLocalization('noActiveTasks')}</p>
                ) : (
                  <ListGroup variant="flush">
                    {instances.map((instance, idx) => (
                      <ListGroup.Item
                        key={instance['row_id'] || idx}
                        action
                        className="px-0 py-2"
                        data-testid={`task-item-${instance.id}`}
                        onClick={() => {
                          setActiveTaskQuestion(question);
                          setActiveTaskDataPoint(instance);
                          setShowTaskModal(true);
                        }}
                      >
                        <div className="task-name mb-1">{String(instance['Name'] ?? '')}</div>
                        <div className="d-flex flex-row task-values">
                          <div className="me-2">
                            {assigned.text} : {instance['assignedto']}
                          </div>
                          <div>
                            {due.text} : {instance['duedate']}
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </div>
            );
          })}

          {referenceTableQuestions.length > 0 && (
            <div className="crm-reference-tables mt-4">
              {referenceTableQuestions.map(question => (
                <div key={question.id} className="mb-4">
                  <h6 className="fw-semibold border-bottom pb-1 mb-2">
                    {question.text || question.id}
                  </h6>
                  {getTableElement(
                    question,
                    localDataPoint,
                    forms,
                    (dp: DataPoint) => setLocalDataPoint(prev => ({ ...prev, ...dp })),
                    getFormUtils(model),
                    clientPersist,
                    undefined,
                    model,
                    false,
                    false
                  )}
                </div>
              ))}
            </div>
          )}
        </Col>
      </Row>

      {activeTaskQuestion && activeSubForm && (
        <GenericModal
          visible={showTaskModal}
          title={activeSubForm.name}
          body={
            <FormComponent
              model={activeSubForm}
              dataPoint={activeTaskDataPoint}
              forms={forms}
              clientPersist={clientPersist}
              readOnly={false}
              parentQuestion={activeTaskQuestion}
              parentModel={model}
              parentDataPoint={formRef.current ? formRef.current.getDataModel() : localDataPoint}
              updateAnswer={dp => setActiveTaskDataPoint(prev => ({...prev, ...dp}))}
            />
          }
          confirmText={getLocalization('save')}
          cancelText={getLocalization('cancel')}
          cancel={() => setShowTaskModal(false)}
          onConfirm={handleTaskSave}
          dialogClassName="modal-lg"
        />
      )}

      {close()}

      {showDeleteConfirm && (
        <ConfirmationModal
          onClose={() => setShowDeleteConfirm(false)}
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          confirmEnabled={deleteConfirmed}
          localizations={{
            header: (
              <label>{getLocalization('confirm')}</label>
            ),
            body: (
              <FormCheck
                id={'datapoint-confirm-delete-btn'}
                checked={deleteConfirmed}
                label={getLocalization('deletePoi')}
                onChange={(e) => setDeleteConfirmed(e.target.checked)}
              />
            ),
            confirm: getLocalization('generaldelete'),
            cancel: getLocalization('cancel'),
            confirmStyle: 'danger'
          }}
        />
      )}
    </>
  );
}
